import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// ── Route Guards ──────────────────────────────────────────────────
import { SuperAdminRoute, AdminRoute, EmployeeRoute } from 'routes/ProtectedRoute';

// ── Auth & Setup ──────────────────────────────────────────────────
import Login        from 'pages/shared/Login';
import InviteSetup  from 'pages/shared/InviteSetup';
import PageNotFound from 'pages/404/404';

// ── Super Admin ───────────────────────────────────────────────────
import SuperAdminLayout   from 'pages/super-admin/SuperAdminLayout';
import CompaniesDashboard from 'pages/super-admin/CompaniesDashboard';

// ── Admin / HR Portal ─────────────────────────────────────────────
import AdminLayout     from 'pages/module/admin/components/Admin/AdminLayout';
import EndJitsiMetting from 'pages/module/admin/meeting/endJitsiCall';
import DownloadAgent     from 'pages/module/admin/components/Admin/DownloadAgent';
import DesktopAgentModule from "pages/module/admin/components/Admin/DesktopAgentModule";
// Skills & Knowledge
import Reports       from 'pages/module/admin/components/Admin/Reports';
import Interviews    from 'pages/module/admin/components/Admin/interviews/Interviews';
import InterviewList from 'pages/module/admin/components/Admin/interviews/interviewsList';
import Questions     from 'pages/module/admin/components/Admin/questions/questions';
import QuestionsList from 'pages/module/admin/components/Admin/questions/questionList';

// Documents
import Documents from 'pages/module/admin/components/Admin/interviews/Documents';


// HRMS Overview
import HrmsDashboard from 'pages/module/admin/components/Admin/hrms/HrmsDashboard';

// Recruitment
import RecruitmentDashboard from 'pages/module/admin/components/Admin/hrms/recruitment/RecruitmentDashboard';
import ApplicantPipeline from 'pages/module/admin/components/Admin/hrms/recruitment/ApplicantPipeline';
import OfferList from 'pages/module/admin/components/Admin/hrms/recruitment/OfferList';

// Employee Management
import EmployeeDirectory     from 'pages/module/admin/components/Admin/hrms/employees/EmployeeDirectory';
import DepartmentManagement  from 'pages/module/admin/components/Admin/hrms/employees/DepartmentManagement';
import DesignationManagement from 'pages/module/admin/components/Admin/hrms/employees/DesignationManagement';
import OnboardingDashboard   from 'pages/module/admin/components/Admin/hrms/employees/OnboardingDashboard';
import AssetInventory        from 'pages/module/admin/components/Admin/hrms/employees/AssetInventory';
import UserManagement     from 'pages/module/admin/components/Admin/hrms/employees/UserManagement';
import ReportingStructure from 'pages/module/admin/components/Admin/hrms/employees/ReportingStructure';


// Attendance & Leave
import AttendanceDashboard from 'pages/module/admin/components/Admin/hrms/attendance/AttendanceDashboard';
import DailyTracker        from 'pages/module/admin/components/Admin/hrms/attendance/DailyTracker';
import LeaveRequests       from 'pages/module/admin/components/Admin/hrms/attendance/LeaveRequests';
import HolidayCalendar     from 'pages/module/admin/components/Admin/hrms/attendance/HolidayCalendar';
import LeavePolicy         from 'pages/module/admin/components/Admin/hrms/attendance/LeavePolicy';
import CompOffAdmin        from 'pages/module/admin/components/Admin/hrms/attendance/CompOffAdmin';


// Payroll
import PayrollDashboard from 'pages/module/admin/components/Admin/hrms/payroll/PayrollDashboard';
import SalaryStructure  from 'pages/module/admin/components/Admin/hrms/payroll/SalaryStructure';
import ProcessPayroll   from 'pages/module/admin/components/Admin/hrms/payroll/ProcessPayroll';
import Payslips         from 'pages/module/admin/components/Admin/hrms/payroll/Payslips';
import TaxDeductions    from 'pages/module/admin/components/Admin/hrms/payroll/TaxDeductions';
import TaxConfig        from 'pages/module/admin/components/Admin/hrms/payroll/TaxConfig';

// Performance
import PerformanceDashboard  from 'pages/module/admin/components/Admin/hrms/performance/PerformanceDashboard';
import PerformanceAppraisals from 'pages/module/admin/components/Admin/hrms/performance/PerformanceAppraisals';
import GoalsKPIs             from 'pages/module/admin/components/Admin/hrms/performance/GoalsKPIs';
import Reviews360            from 'pages/module/admin/components/Admin/hrms/performance/Reviews360';

// Compliance
import ComplianceDocuments from 'pages/module/admin/components/Admin/hrms/compliance/ComplianceDocuments';

// eKYC & Meetings
import EkycDashboard        from 'pages/module/admin/components/Admin/ekyc/dashboard';
import AIEvaluation         from 'pages/module/admin/components/Admin/ekyc/aiEvaluation';
import AuditLogs            from 'pages/module/admin/components/Admin/ekyc/auditLogs';
import DocumentVerification from 'pages/module/admin/components/Admin/ekyc/documentVerification';
import EkycSession          from 'pages/module/admin/components/Admin/ekyc/ekycSession';
import EkycSettings         from 'pages/module/admin/components/Admin/ekyc/settings';
import EkycUserManagement   from 'pages/module/admin/components/Admin/ekyc/userManagement';
import ScheduledMeetingList from 'pages/module/admin/components/Admin/meetings/meetings';
import Recordings           from 'pages/module/admin/components/Admin/meetings/recordings';
import Transcripts          from 'pages/module/admin/components/Admin/meetings/transcripts';
import MeetingSettings      from 'pages/module/admin/components/Admin/meetings/settings';

// Hirality Meetings — standalone Zoom-style scheduling (fresh, independent
// of the eKYC & Meetings prototype above)
import HiralityMeetings     from 'pages/module/admin/components/Admin/hiralityMeetings/Meetings';
import HiralityMeetingDetail from 'pages/module/admin/components/Admin/hiralityMeetings/MeetingDetail';
import HiralityMeetingEnded from 'pages/module/admin/HiralityMeetings/MeetingEnded';

// Sessions
import StartMeetingSession from 'pages/module/admin/meeting/meetingSession';

// Unified session room — one screen for Meeting / Interview / eKYC /
// Healthcare calls, switched by ?type=. /video_chat, /eKYC, and
// /hirality-meeting-room below redirect into it so old links keep working.
import SessionRoom from 'pages/module/admin/SessionRoom/SessionRoom';

// Employee Self-Service
import EmployeeLayout      from 'pages/module/employee/EmployeeLayout';
import EmployeeDashboard   from 'pages/module/employee/EmployeeDashboard';
import EmployeeAttendance  from 'pages/module/employee/EmployeeAttendance';
import EmployeeLeaves      from 'pages/module/employee/EmployeeLeaves';
import EmployeePayroll     from 'pages/module/employee/EmployeePayroll';
import ManagerTeam         from 'pages/module/employee/ManagerTeam';
import EmployeeProfile     from 'pages/module/employee/EmployeeProfile';
import EmployeeHolidays    from 'pages/module/employee/EmployeeHolidays';
import EmployeeDocuments   from 'pages/module/employee/EmployeeDocuments';
import EmployeePerformance from 'pages/module/employee/EmployeePerformance';
import SupportTickets      from 'pages/module/employee/SupportTickets';
import TeamDailyTracker    from 'pages/module/employee/TeamDailyTracker';
import EmployeeReimbursements from "pages/module/employee/EmployeeReimbursements";

//import HiralityGreen       from 'pages/HiralityGreen';
//import HiralityGreen       from 'pages/Hirality_Parallax';
import HiralityGreen       from 'pages/HiralityGreen_Enhanced';
import PrivacyPolicy       from 'pages/PrivacyPolicy';
import TermsOfService      from 'pages/TermsOfService';
import BonusManagement from 'pages/module/admin/components/Admin/hrms/payroll/BonusManagement';
import TDSReports from 'pages/module/admin/components/Admin/hrms/payroll/TDSReports';
import ExpenseTracker from 'pages/module/admin/components/Admin/hrms/expenses/ExpenseTracker';


// Old per-type routes now redirect into the unified /session-room screen,
// preserving every existing query param — old links (e.g. an eKYC
// verification link already sent to a customer) keep working unchanged.
const LegacySessionRedirect = ({ type }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('type', type);
  return <Navigate to={`/session-room?${params.toString()}`} replace />;
};

function Router() {
  return (
    <Routes>

      {/* ── Public routes ── */}
      <Route path="/"              element={<HiralityGreen />} />
      <Route path="/privacy-policy"   element={<PrivacyPolicy />} />
       <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/login"         element={<Login />} />
      <Route path="/setup-account" element={<InviteSetup />} />
      <Route path="/meetingended"  element={<EndJitsiMetting />} />

      {/* ── Sessions (no auth) ── */}
      <Route path="/session-room" element={<SessionRoom />} />
      <Route path="/video_chat" element={<LegacySessionRedirect type="interview" />} />
      <Route path="/eKYC"       element={<LegacySessionRedirect type="ekyc" />} />
      <Route path="/meetings"   element={<StartMeetingSession />} />
      <Route path="/hirality-meeting-room"  element={<LegacySessionRedirect type="meeting" />} />
      <Route path="/hirality-meeting-ended" element={<HiralityMeetingEnded />} />

      {/* ════════════════════════════════════════
          SUPER ADMIN PORTAL
      ════════════════════════════════════════ */}
      <Route path="/super-admin/" element={
        <SuperAdminRoute><SuperAdminLayout /></SuperAdminRoute>
      }>
        <Route path="companies"     element={<CompaniesDashboard />} />
        <Route path="companies/:id" element={<CompaniesDashboard />} />
        <Route path="tickets"       element={<CompaniesDashboard />} />
        <Route path="dashboard"     element={<CompaniesDashboard />} />
        <Route path="billing"       element={<CompaniesDashboard />} />
        <Route path="settings"      element={<CompaniesDashboard />} />
      </Route>

      {/* ════════════════════════════════════════
          COMPANY / HR ADMIN PORTAL
      ════════════════════════════════════════ */}
      <Route path="/admin/" element={
        <AdminRoute><AdminLayout /></AdminRoute>
      }>
    
        <Route path="agent_dashbaord" element={<DesktopAgentModule />} />
        <Route path="agent_download"    element={<DownloadAgent />} />
        
        {/* Dashboard */}
        <Route path="reports" element={<Reports />} />
        <Route path="hrms"    element={<HrmsDashboard />} />

        {/* User & Org Management */}
        <Route path="users/manage"    element={<UserManagement />} />
        <Route path="users/reporting" element={<ReportingStructure />} />

        {/* Recruitment */}
        <Route path="hrms/recruitment" element={<RecruitmentDashboard />} />
        <Route path="hrms/applicants"  element={<ApplicantPipeline />} />
        <Route path="hrms/offers"      element={<OfferList />} />
        <Route path="interviews"                  element={<Interviews />} />
        <Route path="interviews/list/:id"         element={<InterviewList />} />
        <Route path="interviews/list/:id/:status" element={<InterviewList />} />
        <Route path="questions"                   element={<Questions />} />
        <Route path="questions/list/:id"          element={<QuestionsList />} />

        {/* ── Documents (Offer Letters, HR Docs, Logo) ── */}
        <Route path="documents"                element={<Documents />} />
        <Route path="documents/offer-letters"  element={<Documents />} />
        <Route path="documents/other"          element={<Documents />} />
        <Route path="documents/branding"       element={<Documents />} />
        <Route path="documents/branding"       element={<Documents />} />


        {/* Employee Management */}
        <Route path="hrms/employees"    element={<EmployeeDirectory />} />
        <Route path="hrms/departments"  element={<DepartmentManagement />} />
        <Route path="hrms/designations" element={<DesignationManagement />} />
        <Route path="hrms/onboarding"   element={<OnboardingDashboard />} />
        <Route path="hrms/leave-policy" element={<LeavePolicy />} />
        <Route path="/admin/hrms/assets" element={<AssetInventory />} />

        {/* Attendance & Leave */}
        <Route path="hrms/attendance"  element={<AttendanceDashboard />} />
        <Route path="hrms/tracker"     element={<DailyTracker />} />
        <Route path="hrms/leaves"      element={<LeaveRequests />} />
        <Route path="hrms/holidays"    element={<HolidayCalendar />} />
        <Route path="hrms/leave-types" element={<LeaveRequests />} />
        <Route path="hrms/comp-off" element={<CompOffAdmin />} />

        {/* Payroll */}
        <Route path="hrms/payroll"         element={<PayrollDashboard />} />
        <Route path="hrms/salary"          element={<SalaryStructure />} />
        <Route path="hrms/payroll/process" element={<ProcessPayroll />} />
        <Route path="hrms/payslips"        element={<Payslips />} />
        <Route path="hrms/tax"             element={<TaxDeductions />} />
        <Route path="hrms/tds-reports" element={<TDSReports />} />
        <Route path="hrms/tax-config"      element={<TaxConfig />} />
        <Route path="hrms/bonuses" element={<BonusManagement />} />
        {/* Performance */}
        <Route path="hrms/performance"            element={<PerformanceDashboard />} />
        <Route path="hrms/performance/goals"      element={<GoalsKPIs />} />
        <Route path="hrms/performance/appraisals" element={<PerformanceAppraisals />} />
        <Route path="hrms/performance/reviews"    element={<Reviews360 />} />

        {/* Compliance */}
        <Route path="hrms/compliance/documents" element={<ComplianceDocuments />} />

        {/* eKYC */}
        <Route path="ekyc"               element={<EkycDashboard />} />
        <Route path="aievaluation"       element={<AIEvaluation />} />
        <Route path="auditlogs"          element={<AuditLogs />} />
        <Route path="docverification"    element={<DocumentVerification />} />
        <Route path="ekycsession"        element={<EkycSession />} />
        <Route path="ekycsettings"       element={<EkycSettings />} />
        <Route path="ekycusermanagement" element={<EkycUserManagement />} />

        {/* Meetings */}
        <Route path="meetings"       element={<ScheduledMeetingList />} />
        <Route path="recordings"     element={<Recordings />} />
        <Route path="transcripts"    element={<Transcripts />} />
        <Route path="meetingSetting" element={<MeetingSettings />} />
        <Route path="hrms/expenses" element={<ExpenseTracker />} />

        {/* Hirality Meetings */}
        <Route path="hirality-meetings"     element={<HiralityMeetings />} />
        <Route path="hirality-meetings/:mid" element={<HiralityMeetingDetail />} />

      </Route>

      {/* ════════════════════════════════════════
          EMPLOYEE SELF-SERVICE PORTAL
      ════════════════════════════════════════ */}
      <Route path="/employee/" element={
        <EmployeeRoute><EmployeeLayout /></EmployeeRoute>
      }>
        <Route path="dashboard"  element={<EmployeeDashboard />} />
        <Route path="profile"    element={<EmployeeProfile />}  />
        <Route path="support"    element={<SupportTickets />}   />
        <Route path="/employee/reimbursements" element={<EmployeeReimbursements/>}/>

        {/* Attendance & Leave */}
        <Route path="attendance" element={<EmployeeAttendance />} />
        <Route path="leaves"     element={<EmployeeLeaves />}    />
        <Route path="holidays"   element={<EmployeeHolidays />}  />

        <Route path="team/teamtracker" element={<TeamDailyTracker />} />

        {/* Payroll & Tax */}
        <Route path="payslips"   element={<EmployeePayroll />} />
        <Route path="investment" element={<EmployeePayroll />} />

        {/* Documents */}
        <Route path="documents"  element={<EmployeeDocuments />} />

        {/* Performance */}
        <Route path="performance" element={<EmployeePerformance />} />
        <Route path="goals"       element={<EmployeePerformance />} />

        {/* My Team */}
        <Route path="team"            element={<ManagerTeam />} />
        <Route path="team/leaves"     element={<ManagerTeam />} />
        <Route path="team/attendance" element={<ManagerTeam />} />
        <Route path="team/appraisals" element={<ManagerTeam />} />
        <Route path="team/goals"      element={<ManagerTeam />} />
        <Route path="team/compoff"      element={<ManagerTeam />} />

      </Route>

      <Route path="*" element={<PageNotFound />} />

    </Routes>
  );
}

export default Router;